import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { formatDate } from './dateUtils';

const SimpleStackRankTable = ({
                                  items = [],
                                  onReorder,
                                  getDisplayValue,
                                  displayColumns,
                                  onItemClick = () => {},
                                  isRankingEnabled = false
                              }) => {
    const [selectedId, setSelectedId] = useState(null);
    const [localItems, setLocalItems] = useState([]);
    const [isMoving, setIsMoving] = useState(false);
    const moveTimeoutRef = useRef(null);

    useEffect(() => {
        if (!isMoving && Array.isArray(items)) {
            setLocalItems(items);
        }
    }, [items, isMoving]);

    useEffect(() => {
        return () => {
            if (moveTimeoutRef.current) {
                clearTimeout(moveTimeoutRef.current);
            }
        };
    }, []);

    const handleMove = useCallback(async (direction) => {
        if (!isRankingEnabled || !Array.isArray(localItems) || localItems.length === 0) return;

        const currentIndex = localItems.findIndex(item => item.id === selectedId);
        if (currentIndex === -1) return;

        let targetIndex;
        if (direction === 'up') {
            if (currentIndex <= 0) return;
            targetIndex = currentIndex - 1;
        } else {
            if (currentIndex >= localItems.length - 1) return;
            targetIndex = currentIndex + 1;
        }

        const targetId = localItems[targetIndex].id;

        if (moveTimeoutRef.current) {
            clearTimeout(moveTimeoutRef.current);
        }

        try {
            setIsMoving(true);

            const newItems = [...localItems];
            const [movedItem] = newItems.splice(currentIndex, 1);
            newItems.splice(targetIndex, 0, movedItem);
            setLocalItems(newItems);

            moveTimeoutRef.current = setTimeout(async () => {
                try {
                    if (onReorder) {
                        await onReorder(selectedId, targetId);
                    }
                } catch (error) {
                    console.error('Move failed:', error);
                    setLocalItems(items);
                } finally {
                    setIsMoving(false);
                }
            }, 500);
        } catch (error) {
            console.error('Error during move:', error);
            setIsMoving(false);
            setLocalItems(items);
        }
    }, [localItems, selectedId, items, onReorder, isRankingEnabled]);

    useEffect(() => {
        if (!isRankingEnabled || !selectedId) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    handleMove('up');
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    handleMove('down');
                    break;
                case 'Escape':
                    if (moveTimeoutRef.current) {
                        clearTimeout(moveTimeoutRef.current);
                    }
                    setSelectedId(null);
                    setIsMoving(false);
                    setLocalItems(items);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedId, localItems, items, handleMove, isRankingEnabled]);

    const handleClick = (e, item) => {
        if (e.target.closest('.rank-arrows')) return;
        onItemClick(item);
    };

    const handleArrowClick = (e, item) => {
        if (!isRankingEnabled) return;

        e.stopPropagation();

        if (selectedId === item.id) {
            setSelectedId(null);
            if (isMoving && moveTimeoutRef.current) {
                clearTimeout(moveTimeoutRef.current);
                setIsMoving(false);
            }
        } else {
            setSelectedId(item.id);
        }
    };

    const getStateColor = (state) => {
        const stateColors = {
            'New': 'bg-gray-50 text-gray-700',
            'Triaged': 'bg-gray-100 text-gray-800',
            'In Design': 'bg-blue-50 text-blue-700',
            'Design Complete': 'bg-blue-100 text-blue-800',
            'In development': 'bg-blue-200 text-blue-800',
            'QA Ready': 'bg-sky-100 text-sky-800',
            'QA Done': 'bg-teal-100 text-teal-800',
            'In demo environment': 'bg-green-100 text-green-800',
            'In Production': 'bg-green-200 text-green-800',
            'Done': 'bg-green-300 text-green-900'
        };
        return stateColors[state] || 'bg-gray-100 text-gray-700';
    };

    const getFormattedDisplayValue = (item, column) => {
        const value = item.fields[column];
        if (!value || value === '-') return '-';

        // Check if it's a date field
        if (column.toLowerCase().includes('date')) {
            return formatDate(value);
        }

        // Special handling for state column
        if (column === 'System.State') {
            const stateValue = getDisplayValue(item, column);
            return (
                <span className={`inline-block px-2.5 py-1 rounded-md text-sm font-medium ${getStateColor(stateValue)}`}>
                    {stateValue}
                </span>
            );
        }

        return getDisplayValue(item, column);
    };

    if (!Array.isArray(localItems) || localItems.length === 0) {
        return (
            <div className="bg-white p-4 text-center text-gray-500">
                No items to display
            </div>
        );
    }

    return (
        <div className="bg-white">
            <table className="w-full">
                <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                    {isRankingEnabled && <th className="w-8 px-3 py-2" />}
                    {displayColumns.map(column => (
                        <th
                            key={column}
                            className={`px-3 py-2 text-left text-xs font-medium text-gray-500 ${
                                column === 'System.Title' ? '' : 'w-28'
                            }`}
                        >
                            {column.split('.').pop().replace(/([A-Z])/g, ' $1').trim()}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {localItems.map((item) => (
                    <tr
                        key={item.id}
                        onClick={(e) => handleClick(e, item)}
                        className={`group ${
                            selectedId === item.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                        } transition-all duration-150`}
                    >
                        {isRankingEnabled && (
                            <td className="w-8 px-3 py-1.5">
                                <div
                                    className="rank-arrows h-8 flex items-center justify-center cursor-pointer"
                                    onClick={(e) => handleArrowClick(e, item)}
                                >
                                    <ArrowUpDown
                                        size={14}
                                        className={`transition-colors ${
                                            !isRankingEnabled
                                                ? 'text-gray-200 cursor-not-allowed'
                                                : selectedId === item.id
                                                    ? isMoving
                                                        ? 'text-orange-500'
                                                        : 'text-blue-500'
                                                    : 'text-gray-300 group-hover:text-gray-400'
                                        }`}
                                    />
                                </div>
                            </td>
                        )}
                        {displayColumns.map(column => (
                            <td
                                key={column}
                                className={`px-3 py-1.5 text-sm ${
                                    column === 'System.Title' ? 'text-gray-900' : 'text-gray-500'
                                } ${column !== 'System.Title' ? 'whitespace-nowrap' : ''}`}
                            >
                                {getFormattedDisplayValue(item, column)}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default SimpleStackRankTable;